// File: src/pages/PerformanceDashboard.jsx
// Merged: Reports + Demographics + SEO Traffic
// Focus: Revenue trends, audience breakdown, traffic sources, strategic prescriptions

import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  SkeletonPage,
  SkeletonBodyText,
  Banner,
  Box,
  Tooltip,
  Icon,
} from '@shopify/polaris';
import { ArrowLeftIcon, QuestionCircleIcon } from '@shopify/polaris-icons';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid
} from 'recharts';
import { Info } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { cleanText } from '../utils/cleanText';
import DeviceChart from '../components/Charts/DeviceChart';
import LayoutWrapper from '../components/Layout/Layout';
import CountryChoroplethMap from '../components/Charts/CountryChoroplethMap';
import MultiLineChart from '../components/Charts/MultiLineChart';
import BarChartComponent from '../components/Charts/BarChart';
import useData from '../hooks/useData';
import PrescriptionBanner from '../components/Prescriptions/PrescriptionBanner';
import GA4SetupPrompt from '../components/GA4SetupPrompt';
import '../components/Layout/ui-shell.css';

const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

// --- PageGoal component with plain text ---
function PageGoal({ goal }) {
  return (
    <Text as="p" tone="subdued">
      <Text as="span" fontWeight="bold">{cleanText("Page Goal:")}</Text> {cleanText(goal)}
    </Text>
  );
}

// --- SectionTitle component for consistent headings ---
function SectionTitle({ chip, title, tone = 'purple' }) {
  const bg = tone === 'teal' ? 'var(--accent-2)' : 'var(--accent)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <div style={{
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: 6,
        background: bg,
        color: 'white',
        fontSize: 12,
        fontWeight: 'bold',
        letterSpacing: 0.3,
      }}>
        {chip}
      </div>
      <Text variant="headingMd" as="h3">{title}</Text>
    </div>
  );
}

// Helper for YYYYMMDD date strings
const ymd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
};

const lastNDaysYMD = (n = 7) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Array.from({ length: n }, (_, i) => {
    const dt = new Date(today);
    dt.setDate(dt.getDate() - (n - 1 - i));
    return ymd(dt);
  });
};

// KPI helpers
const sumField = (rows, startIdx, endIdx, field) =>
  rows.slice(startIdx, endIdx).reduce((t, r) => t + Number(r[field] || 0), 0);

const last15vsPrev15 = (rows, field) => {
  const arr = Array.isArray(rows) ? [...rows].sort((a, b) => new Date(a.date) - new Date(b.date)) : [];
  const n = arr.length;
  if (n === 0) return { curr: 0, prev: 0, deltaPct: 0 };
  const last30Start = Math.max(0, n - 30);
  const last15Start = Math.max(0, n - 15);
  const prev = sumField(arr, last30Start, last15Start, field);
  const curr = sumField(arr, last15Start, n, field);
  const deltaPct = prev > 0 ? ((curr - prev) / prev) * 100 : (curr > 0 ? Number.POSITIVE_INFINITY : 0);
  return { curr, prev, deltaPct };
};

// Calculate KPIs from raw sales/order data
const calculateKPIsFromOrders = (orders) => {
  if (!orders || !Array.isArray(orders) || orders.length === 0) {
    return { curr: 0, prev: 0, deltaPct: 0, currOrders: 0, prevOrders: 0, aovCurr: 0, aovPrev: 0, aovDeltaPct: 0 };
  }
  
  const now = new Date();
  const fifteenDaysAgo = new Date(now);
  fifteenDaysAgo.setDate(now.getDate() - 15);
  const thirtyDaysAgo = new Date(now);
  thirtyDaysAgo.setDate(now.getDate() - 30);
  
  let currRevenue = 0, prevRevenue = 0, currOrders = 0, prevOrders = 0;
  
  orders.forEach(order => {
    const orderDate = new Date(order.created_at || order.date_created || order.date);
    const amount = Number(order.total_price || order.total || order.amount || 0);
    
    if (orderDate >= fifteenDaysAgo) {
      currRevenue += amount;
      currOrders += 1;
    } else if (orderDate >= thirtyDaysAgo) {
      prevRevenue += amount;
      prevOrders += 1;
    }
  });
  
  const deltaPct = prevRevenue > 0 
    ? ((currRevenue - prevRevenue) / prevRevenue) * 100 
    : (currRevenue > 0 ? Number.POSITIVE_INFINITY : 0);
  
  const aovCurr = currOrders > 0 ? currRevenue / currOrders : 0;
  const aovPrev = prevOrders > 0 ? prevRevenue / prevOrders : 0;
  const aovDeltaPct = aovPrev > 0 ? ((aovCurr - aovPrev) / aovPrev) * 100 : 0;
  
  return { 
    curr: currRevenue, 
    prev: prevRevenue, 
    deltaPct, 
    currOrders, 
    prevOrders,
    aovCurr,
    aovPrev,
    aovDeltaPct
  };
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}>
        <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ margin: '4px 0', color: p.color || 'inherit' }}>
            {cleanText(`${p.name}: ${p.value}`)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Business stage helper
function getBusinessStage(totalRevenue, totalOrders) {
  if (totalRevenue < 1000 || totalOrders < 10) return 'early';
  if (totalRevenue < 10000 || totalOrders < 100) return 'growing';
  if (totalRevenue < 50000 || totalOrders < 500) return 'scaling';
  return 'established';
}

const stageLabels = {
  early: 'Early Stage',
  growing: 'Growing',
  scaling: 'Scaling Up',
  established: 'Established'
};

// SEO Modal content
const seoModalContent = {
  title: 'What is SEO Traffic?',
  body: `SEO traffic shows how people are finding your site through search engines or by visiting it directly. We break it down by channel to help you understand where your traffic is coming from.

**Traffic Channels Explained:**

- **Organic Search:** Visitors who found your site by searching on Google, Bing, etc.—without clicking an ad. This often includes traffic from Google Shopping's free product listings.
- **Direct Traffic:** Visitors who typed your URL, used a bookmark, or came from an untrackable source (e.g., a document or SMS).
- **Referral:** Visitors from links on other websites (not social networks or ads).
- **Organic Social:** Visitors from unpaid social posts or profile links.
- **Paid Search:** Visitors from search ads (Google Ads/Bing Ads).

**Why it matters:**

Understanding this breakdown helps you:

- Measure the success of your SEO efforts.
- See how many loyal users return directly.
- Identify which partners, posts, or campaigns are driving growth.
- Spot when traffic is dropping or growing from specific channels.`
};

// ============================================
// PRESCRIPTIVE SMART ACTIONS (Beehive-style)
// ============================================
function generateSmartActions({
  totalRevenue, totalOrders, totalSessions, conversionRate, bounceRate, 
  netSalesDirection, aovCurrent, aovDeltaPct, topProducts,
  deviceCounts, newCount, returningCount, peakHour, topLanguage, bounceRateByDevice
}) {
  const actions = [];
  const stage = getBusinessStage(totalRevenue, totalOrders);
  const cr = parseFloat(conversionRate) || 0;
  const aov = totalOrders > 0 ? totalRevenue / totalOrders : aovCurrent;
  const totalDevices = (deviceCounts?.mobile || 0) + (deviceCounts?.desktop || 0) + (deviceCounts?.tablet || 0);
  const mobilePct = totalDevices > 0 ? ((deviceCounts?.mobile || 0) / totalDevices * 100) : 0;
  const returningPct = totalSessions > 0 ? ((returningCount || 0) / totalSessions * 100) : 0;
  const mobileBounce = bounceRateByDevice?.mobile || 0;

  // EARLY STAGE: Get first sales
  if (stage === 'early' && totalOrders === 0) {
    actions.push({
      priority: 'critical',
      label: 'Get Your First Sale',
      action: `Add trust signals: customer reviews, money-back guarantee badge, secure checkout icons.`
    });
    actions.push({
      priority: 'critical',
      label: 'Reduce Checkout Friction',
      action: `Enable guest checkout. Test purchase flow on mobile. Remove unnecessary form fields.`
    });
    if (totalSessions > 100) {
      actions.push({
        priority: 'high',
        label: 'Capture Leaving Visitors',
        action: `Add exit-intent popup: "Get 10% off your first order" with email capture.`
      });
    }
  }

  // MOBILE FRICTION
  if (mobilePct > 50 && mobileBounce > 50) {
    actions.push({
      priority: 'critical',
      label: 'Fix Mobile Experience',
      action: `${mobileBounce.toFixed(0)}% mobile bounce rate. Compress images, enable Apple Pay/Google Pay, simplify navigation.`
    });
  } else if (mobilePct > 50 && cr < 1.5) {
    actions.push({
      priority: 'critical',
      label: 'Mobile Checkout Issue',
      action: `${mobilePct.toFixed(0)}% mobile traffic, ${cr}% conversion. Test checkout on your phone—find the friction point.`
    });
  }

  // DESKTOP OPTIMIZATION
  if (mobilePct < 40 && aov < 75 && totalOrders > 0) {
    actions.push({
      priority: 'high',
      label: 'Increase Desktop AOV',
      action: `Desktop shoppers research more. Add comparison tables, detailed FAQs, and "Frequently Bought Together" sections.`
    });
  }

  // RETENTION
  if (returningPct < 15 && totalOrders > 5) {
    actions.push({
      priority: 'critical',
      label: 'Build Repeat Customers',
      action: `Only ${returningPct.toFixed(0)}% returning visitors. Set up post-purchase email sequence with product tips and reorder reminders.`
    });
  } else if (returningPct < 25 && totalSessions > 200) {
    actions.push({
      priority: 'high',
      label: 'Capture Email Addresses',
      action: `Add homepage popup: 10% off first order for email signup. Build your list before you lose these visitors.`
    });
  }

  // PEAK HOUR TIMING
  if (peakHour !== null && totalSessions > 50) {
    const scheduleHour = peakHour > 0 ? peakHour - 1 : 23;
    actions.push({
      priority: 'medium',
      label: 'Time Your Marketing',
      action: `Peak traffic at ${peakHour}:00. Schedule emails and social posts for ${scheduleHour}:00 to catch visitors as they arrive.`
    });
  }

  // AOV OPTIMIZATION
  if (aov > 0 && aov < 40 && totalOrders > 10) {
    actions.push({
      priority: 'high',
      label: 'Raise Average Order',
      action: `$${aov.toFixed(0)} AOV. Add free shipping threshold at $${Math.round(aov * 1.4)} or bundle deals to increase cart size.`
    });
  }

  // LOCALIZATION
  if (topLanguage && topLanguage.lang !== 'en' && topLanguage.count > 30) {
    actions.push({
      priority: 'medium',
      label: 'Localization Opportunity',
      action: `${topLanguage.count} visitors speak ${topLanguage.lang.toUpperCase()}. Add translated product descriptions for 10-20% conversion lift.`
    });
  }

  // CONVERSION CRISIS
  if (cr < 1 && totalSessions > 300 && stage !== 'early') {
    actions.push({
      priority: 'critical',
      label: 'Conversion Emergency',
      action: `${cr}% conversion = 99% of visitors leave without buying. Pause ad spend. Fix: pricing, shipping costs, or site trust issues.`
    });
  }

  // REVENUE DECLINE
  if (netSalesDirection < -15 && totalOrders > 10) {
    actions.push({
      priority: 'critical',
      label: 'Revenue Dropping',
      action: `Sales down ${Math.abs(netSalesDirection).toFixed(0)}%. Check: ad performance, inventory issues, competitor pricing, or broken checkout.`
    });
  }

  // SCALE OPPORTUNITY
  if (cr > 2.5 && netSalesDirection > 10 && stage !== 'early') {
    actions.push({
      priority: 'high',
      label: 'Scale What Works',
      action: `${cr}% conversion with sales up ${netSalesDirection.toFixed(0)}%. Your funnel is healthy—increase ad spend 20-30%.`
    });
  }

  // TOP PRODUCT
  if (topProducts?.[0] && stage === 'early') {
    actions.push({
      priority: 'high',
      label: 'Hero Product Strategy',
      action: `"${topProducts[0].product_name}" is gaining traction. Feature it prominently on homepage with urgency messaging.`
    });
  }

  // Sort by priority and return top 4
  const priorityOrder = { critical: 0, high: 1, medium: 2 };
  return actions
    .sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority])
    .slice(0, 4);
}

// ============================================
// BIGGEST OPPORTUNITY (Beehive-style)
// ============================================
function getBiggestOpportunity({
  totalRevenue, totalOrders, totalSessions, conversionRate, 
  netSalesDirection, aovCurrent, deviceCounts, returningCount, newCount
}) {
  const stage = getBusinessStage(totalRevenue, totalOrders);
  const cr = parseFloat(conversionRate) || 0;
  const aov = totalOrders > 0 ? totalRevenue / totalOrders : aovCurrent;
  const totalDevices = (deviceCounts?.mobile || 0) + (deviceCounts?.desktop || 0);
  const mobilePct = totalDevices > 0 ? ((deviceCounts?.mobile || 0) / totalDevices * 100) : 0;
  const returningPct = totalSessions > 0 ? ((returningCount || 0) / totalSessions * 100) : 0;

  if (stage === 'early' && totalOrders === 0) {
    return `Zero sales with ${totalSessions} visitors. Priority #1: Add trust signals and simplify checkout to convert your first customer.`;
  }

  if (stage === 'early') {
    return `Early stage: Focus on (1) trust builders—reviews, guarantees, (2) frictionless checkout, (3) email capture for retargeting.`;
  }

  if (mobilePct > 55 && cr < 1.5) {
    return `${mobilePct.toFixed(0)}% of traffic is mobile but conversion is ${cr}%. Fix mobile checkout to potentially double revenue.`;
  }

  if (returningPct < 15 && cr > 1.5) {
    return `Good conversion (${cr}%) but only ${returningPct.toFixed(0)}% return. Post-purchase emails + loyalty program = 30%+ revenue lift without more ad spend.`;
  }

  if (netSalesDirection < -15) {
    return `Revenue down ${Math.abs(netSalesDirection).toFixed(0)}%. Diagnose now: traffic problem (check ads/SEO) or conversion problem (check site/pricing).`;
  }

  if (cr > 2.5 && netSalesDirection > 5) {
    return `Healthy ${cr}% conversion with positive momentum. Scale your winning channels—increase ad spend and expand product line.`;
  }

  if (aov < 50 && totalOrders > 20) {
    return `$${aov.toFixed(0)} average order is leaving money on the table. Add bundles, upsells, or free shipping threshold at $${Math.round(aov * 1.4)}.`;
  }

  return `Focus areas: (1) Retention—email flows and loyalty, (2) AOV—bundles and thresholds, (3) Scale—increase spend on winning channels.`;
}

export default function PerformanceDashboard() {
  const { token } = useContext(AuthContext);
  const [siteId, setSiteId] = useState(() =>
    sessionStorage.getItem('activeSiteId') || sessionStorage.getItem('siteId') || sessionStorage.getItem('site_id') || ''
  );

  // Modal state for SEO info
  const [modalActive, setModalActive] = useState(false);
  const handleOpenModal = useCallback(() => setModalActive(true), []);
  const handleCloseModal = useCallback(() => setModalActive(false), []);

  useEffect(() => {
    const read = () => sessionStorage.getItem('activeSiteId') || sessionStorage.getItem('siteId') || sessionStorage.getItem('site_id') || '';
    if (!siteId) setSiteId(read());
    const onStorage = (e) => { if (['activeSiteId', 'siteId', 'site_id'].includes(e.key)) setSiteId(read()); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [siteId]);

  // Fetch GA4 property
  const { data: ga4Prop } = useData(
    `ga4PropertyId:${token}`,
    () => fetch(`${API}/integrations/property`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token] }
  );
  const propertyId = ga4Prop?.propertyId;

  // Fetch GA4 status
  const { data: gaStatusData, loading: gaLoading } = useData(
    token ? `integrationStatus:${token}` : 'noop',
    () => fetch(`${API}/integrations/status`, { headers: { Authorization: `Bearer ${token}` } }).then(res => res.ok ? res.json() : { connected: false }),
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token] }
  );
  const gaStatus = useMemo(() => ({ loading: gaLoading, connected: gaStatusData?.connected || false }), [gaLoading, gaStatusData]);

  // Fetch dashboard summary (same endpoint as Dashboard.jsx for accurate revenue/orders)
  const { data: dashboardSummary, loading: summaryLoading } = useData(
    siteId ? `dashboard-summary:${siteId}:30` : 'noop',
    () => fetch(`${API}/api/dashboard/summary/detailed?site_id=${siteId}&days=30&force=1&_=${Date.now()}`, {
      headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-store', 'Pragma': 'no-cache' },
      cache: 'no-store',
    }).then(r => r.json()),
    { ttlMs: 60 * 60 * 1000, deps: [siteId, token] }
  );

  // Fetch sessions data (for visit count)
  const { data: sessionsData } = useData(
    siteId ? `businessSessions:${siteId}:30` : 'noop',
    () => fetch(`${API}/api/business/sessions?site_id=${siteId}&days=30`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.json()),
    { ttlMs: 60 * 60 * 1000, deps: [siteId, token] }
  );

  // Fetch report data (for timeseries charts)
  const { data: reportData, loading: reportLoading, error: reportError } = useData(
    siteId ? `report:${siteId}:30days` : 'noop',
    async () => {
      const headers = { headers: { Authorization: `Bearer ${token}` } };
      const [summaryRes, timeseriesRes] = await Promise.all([
        fetch(`${API}/api/analytics/report/summary?site_id=${siteId}&start_date=30daysAgo&end_date=today`, headers),
        fetch(`${API}/api/analytics/report/timeseries?site_id=${siteId}&start_date=60daysAgo&end_date=today`, headers)
      ]);
      const summary = summaryRes.ok ? await summaryRes.json() : null;
      const dailyRows = timeseriesRes.ok ? await timeseriesRes.json() : [];
      return { summary: summary || {}, dailyRows: dailyRows || [] };
    },
    { ttlMs: 24 * 60 * 60 * 1000, deps: [siteId, token] }
  );

  // Fetch demographics data
  const { data: demoData, loading: demoLoading, error: demoError } = useData(
    siteId ? `demographics:${siteId}:7days` : 'noop',
    async () => {
      const res = await fetch(`${API}/api/analytics/demographics?site_id=${siteId}&days=7`, {
        headers: { Accept: 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const json = await res.json();
      const processedLanguageCounts = {};
      for (const langTag in json.languageCounts) {
        if (json.languageCounts.hasOwnProperty(langTag) && langTag !== 'Unknown') {
          let baseLangCode = langTag.toLowerCase().split('-')[0];
          processedLanguageCounts[baseLangCode] = (processedLanguageCounts[baseLangCode] || 0) + json.languageCounts[langTag];
        }
      }
      return { ...json, languageCounts: processedLanguageCounts };
    },
    { ttlMs: 24 * 60 * 60 * 1000, deps: [siteId, token] }
  );

  // Fetch SEO traffic data (exact same as InsightsPage)
  const { data: ga4ReportData, loading: seoLoading } = useData(
    propertyId ? `ga4Report:${propertyId}:7days` : 'noop',
    () => fetch(`${API}/integrations/report?start_date=7daysAgo&end_date=today&limit=100`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => res.ok ? res.json() : { rows: [] }),
    { ttlMs: 24 * 60 * 60 * 1000, deps: [propertyId, token] }
  );

  // Fetch top products
  const { data: ciTopProducts } = useData(
    siteId ? `customer-intel:top-products:${siteId}:30` : 'noop',
    siteId ? async () => {
      const r = await fetch(`${API}/api/customer-intelligence/top-products?site_id=${siteId}&days=30`, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' });
      return r.ok ? r.json() : null;
    } : null,
    { ttlMs: 60 * 60 * 1000, deps: [siteId, token] }
  );

  // Fetch sales data - supports Shopify, WooCommerce, and BigCommerce
  const platform = sessionStorage.getItem('site_platform');
  const siteToken = sessionStorage.getItem('site_token');
  const siteUrl = sessionStorage.getItem('site_url');

  const { data: salesRawData } = useData(
    siteId && siteToken && platform ? `sales:${siteId}:${platform}:30days` : 'noop',
    async () => {
      let salesPath;
      switch ((platform || '').toLowerCase()) {
        case 'shopify': 
          salesPath = `/analytics/shopify/orders?site_id=${siteId}&site_token=${siteToken}&days=30`; 
          break;
        case 'woocommerce': {
          const secret = sessionStorage.getItem('site_consumer_secret');
          if (!secret) return null;
          salesPath = `/analytics/woocommerce/orders?store_url=${encodeURIComponent(siteUrl)}&consumer_key=${siteToken}&consumer_secret=${secret}&days=30`;
          break;
        }
        case 'bigcommerce': {
          const storeHash = sessionStorage.getItem('bigcommerce_store_hash');
          if (!storeHash) return null;
          salesPath = `/analytics/bigcommerce/orders?store_hash=${encodeURIComponent(storeHash)}&token=${siteToken}&days=30`;
          break;
        }
        default: 
          // Try a generic orders endpoint if available
          salesPath = `/api/orders?site_id=${siteId}&days=30`;
      }
      const res = await fetch(`${API}${salesPath}`, { headers: { Authorization: `Bearer ${token}` } });
      return res.ok ? res.json() : null;
    },
    { ttlMs: 60 * 60 * 1000, deps: [siteId, siteToken, platform, token] }
  );

  // Process metrics - Use dashboardSummary for revenue/orders (same as Dashboard.jsx)
  const dashboardOrders = dashboardSummary?.orders_7d ?? dashboardSummary?.metrics?.orders_7d ?? dashboardSummary?.orders?.last7d?.count ?? dashboardSummary?.ordersLast7d ?? dashboardSummary?.orders_last_7d ?? 0;
  const dashboardRevenue = dashboardSummary?.sales_7d ?? dashboardSummary?.metrics?.sales_7d ?? dashboardSummary?.sales?.last7d?.amount ?? dashboardSummary?.revenue_7d ?? dashboardSummary?.revenueLast7d ?? dashboardSummary?.sales_last_7d ?? 0;
  
  // Use sessions from sessionsData (same as Dashboard.jsx)
  const sessionsCount = sessionsData?.sessions ?? sessionsData?.total ?? 0;
  
  // Fallback to report data if dashboard summary is empty
  const { totalSessions: reportSessions = 0, totalRevenue: reportRevenue = 0, totalOrders: reportOrders = 0, bounceRate = 0 } = reportData?.summary || {};
  const { totalSessions: demoSessions = 0, newCount = 0, returningCount = 0, languageCounts = {}, deviceCounts = {}, sessionsByHour = {}, bounceRateByDevice = {} } = demoData || {};
  
  // ========================================
  // IMPROVED KPI CALCULATIONS
  // ========================================
  
  // Calculate KPIs from multiple sources with fallbacks
  const salesOrdersKPIs = useMemo(() => {
    // First try: Use raw orders data (most accurate)
    if (salesRawData?.orders && Array.isArray(salesRawData.orders) && salesRawData.orders.length > 0) {
      return calculateKPIsFromOrders(salesRawData.orders);
    }
    
    // Second try: Use report timeseries data
    if (reportData?.dailyRows && Array.isArray(reportData.dailyRows) && reportData.dailyRows.length > 0) {
      const salesTrend = last15vsPrev15(reportData.dailyRows, 'revenue');
      const ordersTrend = last15vsPrev15(reportData.dailyRows, 'orders');
      const aovCurr = salesTrend.curr > 0 && ordersTrend.curr > 0 ? (salesTrend.curr / ordersTrend.curr) : 0;
      const aovPrev = salesTrend.prev > 0 && ordersTrend.prev > 0 ? (salesTrend.prev / ordersTrend.prev) : 0;
      const aovDeltaPct = aovPrev > 0 ? ((aovCurr - aovPrev) / aovPrev) * 100 : 0;
      
      return {
        curr: salesTrend.curr,
        prev: salesTrend.prev,
        deltaPct: salesTrend.deltaPct,
        currOrders: ordersTrend.curr,
        prevOrders: ordersTrend.prev,
        aovCurr,
        aovPrev,
        aovDeltaPct
      };
    }
    
    // Fallback: Use dashboard summary totals
    const totalRev = Number(dashboardRevenue) || Number(reportRevenue) || 0;
    const totalOrd = Number(dashboardOrders) || Number(reportOrders) || 0;
    const aov = totalOrd > 0 ? totalRev / totalOrd : 0;
    
    return {
      curr: totalRev,
      prev: 0,
      deltaPct: totalRev > 0 ? Number.POSITIVE_INFINITY : 0,
      currOrders: totalOrd,
      prevOrders: 0,
      aovCurr: aov,
      aovPrev: 0,
      aovDeltaPct: 0
    };
  }, [salesRawData, reportData, dashboardRevenue, dashboardOrders, reportRevenue, reportOrders]);
  
  // Use calculated KPIs
  const netSalesDirection = salesOrdersKPIs.deltaPct;
  const aovCurr = salesOrdersKPIs.aovCurr;
  const aovDeltaPct = salesOrdersKPIs.aovDeltaPct;
  
  // Use dashboard values first, then fall back to calculated values
  const totalRevenue = Number(dashboardRevenue) || salesOrdersKPIs.curr || Number(reportRevenue) || 0;
  const totalOrders = Number(dashboardOrders) || salesOrdersKPIs.currOrders || Number(reportOrders) || 0;
  const totalSessions = sessionsCount || reportSessions || demoSessions || 0;
  const conversionRate = totalSessions > 0 ? ((totalOrders / totalSessions) * 100).toFixed(1) : '0.0';

  // Process sales chart data
  const salesChartData = useMemo(() => {
    // First try: Use raw orders data
    if (salesRawData?.orders && Array.isArray(salesRawData.orders) && salesRawData.orders.length > 0) {
      const grouped = {};
      const now = new Date();
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(now.getDate() - 30);
      
      salesRawData.orders.forEach(order => {
        const orderDate = new Date(order.created_at || order.date_created || order.date);
        if (orderDate >= thirtyDaysAgo) {
          const dateKey = orderDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          const amount = Number(order.total_price || order.total || order.amount || 0);
          grouped[dateKey] = (grouped[dateKey] || 0) + amount;
        }
      });
      
      const chartData = Object.entries(grouped).map(([name, value]) => ({ name, value }));
      if (chartData.length > 0) return chartData;
    }
    
    // Second try: Use report timeseries
    if (reportData?.dailyRows && Array.isArray(reportData.dailyRows)) {
      const last30Days = reportData.dailyRows.slice(-30);
      const formattedData = last30Days.map(row => ({ 
        name: new Date(row.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), 
        value: Number(row.revenue || 0) 
      }));
      if (formattedData.some(d => d.value > 0)) return formattedData;
    }
    
    // Third try: Use dashboardSummary to show at least today's and period totals
    // This creates a simple visualization when detailed daily data isn't available
    if (dashboardSummary) {
      const chartData = [];
      const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      // Get today's sales
      const todaySales = Number(dashboardSummary.sales_today || dashboardSummary.sales?.today?.amount || 0);
      
      // Get period sales (current_period from API response)
      const periodSales = Number(
        dashboardSummary.orders?.current_period?.revenue || 
        dashboardSummary.sales?.current_period?.amount ||
        dashboardSummary.sales_7d ||
        dashboardSummary.revenue_7d ||
        0
      );
      
      // If we have today's sales, show it
      if (todaySales > 0) {
        chartData.push({ name: today, value: todaySales });
      }
      
      // If we have period sales and it's different from today
      if (periodSales > 0 && periodSales !== todaySales) {
        // Show as "Last 30d avg" to indicate it's aggregated
        const avgDaily = periodSales / 30;
        if (avgDaily > 0) {
          chartData.push({ name: 'Avg/day', value: Math.round(avgDaily * 100) / 100 });
        }
      }
      
      // If we only have total data, show it as a single bar
      if (chartData.length === 0 && periodSales > 0) {
        chartData.push({ name: 'Total (30d)', value: periodSales });
      }
      
      if (chartData.length > 0) return chartData;
    }
    
    return [];
  }, [salesRawData, reportData, dashboardSummary]);

  // Process SEO traffic data (exact same as InsightsPage)
  const { sessionsByChannelData, channels } = useMemo(() => {
    const dates = lastNDaysYMD(7);
    const rows = ga4ReportData?.rows || [];
    const channelSet = new Set();
    const byDayChannel = {};
    rows.forEach(r => {
      const d = r.date;
      const ch = r.channel || 'Unknown';
      channelSet.add(ch);
      if (!byDayChannel[d]) byDayChannel[d] = {};
      byDayChannel[d][ch] = (byDayChannel[d][ch] || 0) + (r.sessions || 0);
    });
    const channelsArr = [...channelSet];
    const chartData = dates.map(d => {
      const obj = { date: `${d.slice(4, 6)}/${d.slice(6)}` };
      channelsArr.forEach(ch => { obj[ch] = byDayChannel[d]?.[ch] || 0; });
      return obj;
    });
    return { sessionsByChannelData: chartData, channels: channelsArr };
  }, [ga4ReportData]);

  // Process demographics data
  const hourlyData = useMemo(() => Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    label: `${String(i).padStart(2, '0')}:00`,
    count: sessionsByHour[i] || 0
  })), [sessionsByHour]);

  const peakHour = useMemo(() => {
    const max = Math.max(...hourlyData.map(h => h.count));
    return max > 0 ? hourlyData.find(h => h.count === max)?.hour : null;
  }, [hourlyData]);

  const loyaltyData = [
    { name: 'New', value: newCount || 0 },
    { name: 'Returning', value: returningCount || 0 }
  ];

  // Find top language
  const topKnownLanguage = useMemo(() => {
    const entries = Object.entries(languageCounts || {}).filter(([lang]) => lang !== 'unknown');
    if (entries.length === 0) return null;
    const sorted = entries.sort((a, b) => b[1] - a[1]);
    return { lang: sorted[0][0], count: sorted[0][1] };
  }, [languageCounts]);

  // Generate smart actions with all available data
  const smartActions = useMemo(() => generateSmartActions({
    totalRevenue, totalOrders, totalSessions, conversionRate, bounceRate,
    netSalesDirection, aovCurrent: aovCurr, aovDeltaPct, topProducts: ciTopProducts,
    deviceCounts, newCount, returningCount, peakHour, topLanguage: topKnownLanguage, bounceRateByDevice
  }), [totalRevenue, totalOrders, totalSessions, conversionRate, bounceRate, netSalesDirection, aovCurr, aovDeltaPct, ciTopProducts, deviceCounts, newCount, returningCount, peakHour, topKnownLanguage, bounceRateByDevice]);

  const biggestOpportunity = useMemo(() => getBiggestOpportunity({
    totalRevenue, totalOrders, totalSessions, conversionRate,
    netSalesDirection, aovCurrent: aovCurr, deviceCounts, returningCount, newCount
  }), [totalRevenue, totalOrders, totalSessions, conversionRate, netSalesDirection, aovCurr, deviceCounts, returningCount, newCount]);

  const stage = getBusinessStage(totalRevenue, totalOrders);
  const loading = reportLoading || demoLoading || seoLoading || summaryLoading;
  const error = reportError || demoError;

  // Determine if we have any sales data
  const hasSalesData = totalRevenue > 0 || totalOrders > 0 || salesChartData.length > 0;
  const hasRecentSales = salesOrdersKPIs.curr > 0 || salesOrdersKPIs.currOrders > 0;

  if (!siteId) {
    return (
      <LayoutWrapper>
        <Page title={cleanText("Performance Dashboard")} backAction={{ content: cleanText('Back'), onAction: () => window.history.back(), icon: ArrowLeftIcon }}>
          <Layout.Section><Banner status="warning" title={cleanText("No Site Selected")}>{cleanText("No site selected. Open your dashboard and choose a site to continue.")}</Banner></Layout.Section>
        </Page>
      </LayoutWrapper>
    );
  }

  if (loading) {
    return (
      <LayoutWrapper>
        <Page title={cleanText("Performance Dashboard")} backAction={{ content: cleanText('Back'), onAction: () => window.history.back(), icon: ArrowLeftIcon }}>
          <Layout.Section><SkeletonPage /></Layout.Section>
          <Layout.Section><Card sectioned><SkeletonBodyText lines={8} /></Card></Layout.Section>
        </Page>
      </LayoutWrapper>
    );
  }

  if (error) {
    return (
      <LayoutWrapper>
        <Page title={cleanText("Performance Dashboard")} backAction={{ content: cleanText('Back'), onAction: () => window.history.back(), icon: ArrowLeftIcon }}>
          <Banner status="critical" title={cleanText("Error loading data")}><p>{cleanText(String(error))}</p></Banner>
        </Page>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <Page
        title={cleanText("Performance Dashboard")}
        subtitle={cleanText(`Site ID: ${siteId}`)}
        backAction={{ content: cleanText('Back'), onAction: () => window.history.back(), icon: ArrowLeftIcon }}
      >
        <PageGoal goal={cleanText("Monitor revenue trends, audience breakdown & traffic sources to identify strategic opportunities for growth.")} />
        <div style={{ height: 4, width: '100%', borderRadius: 4, margin: '4px 0 12px', background: 'linear-gradient(90deg, var(--accent), var(--accent-2))' }} />
        <PrescriptionBanner siteId={siteId} token={token} category="growth" />

        <Layout>
          {/* Early Stage Guidance Banner */}
          {!hasSalesData && totalSessions > 0 && (
            <Layout.Section>
              <Banner status="warning" title="No Sales Yet - Here's What To Do">
                <p>You have {totalSessions} visitors but zero orders. Your first priority is getting that first sale.</p>
              </Banner>
            </Layout.Section>
          )}

          {/* KPI Snapshot - 3 KPIs only */}
          <Layout.Section>
            <Card title={cleanText("Performance Snapshot")} sectioned>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                
                {/* Total Orders */}
                <div style={{ padding: 20, borderRadius: 12, background: 'rgba(106, 13, 173, 0.08)', border: '1px solid rgba(106, 13, 173, 0.25)' }}>
                  <div style={{ fontSize: 28, fontWeight: 'bold', color: '#6A0DAD', marginBottom: 4 }}>
                    {totalOrders}
                  </div>
                  <Text variant="bodyMd" as="h3">{cleanText("Total Orders")}</Text>
                  <Text variant="bodySm" tone="subdued">{cleanText("Last 30 days")}</Text>
                </div>

                {/* AOV */}
                <div style={{ padding: 20, borderRadius: 12, background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)' }}>
                  <div style={{ fontSize: 28, fontWeight: 'bold', color: '#f59e0b', marginBottom: 4 }}>
                    {totalOrders > 0 ? `$${(totalRevenue / totalOrders).toFixed(0)}` : '—'}
                  </div>
                  <Text variant="bodyMd" as="h3">{cleanText("Avg Order Value")}</Text>
                  <Text variant="bodySm" tone="subdued">{cleanText(totalOrders > 0 ? "Revenue ÷ Orders" : "Need orders to calculate")}</Text>
                </div>

                {/* Conversion Rate */}
                <div style={{ padding: 20, borderRadius: 12, background: 'rgba(0, 168, 169, 0.08)', border: '1px solid rgba(0, 168, 169, 0.25)' }}>
                  <div style={{ fontSize: 28, fontWeight: 'bold', color: '#00A8A9', marginBottom: 4 }}>{conversionRate}%</div>
                  <Text variant="bodyMd" as="h3">{cleanText("Conversion Rate")}</Text>
                  <Text variant="bodySm" tone="subdued">{cleanText(`${totalOrders} orders ÷ ${totalSessions.toLocaleString()} visits`)}</Text>
                </div>
              </div>
              
              {/* Trend indicators - only show if we have comparison data */}
              {hasRecentSales && (
                <div style={{ marginTop: 16, padding: 12, background: 'rgba(0,0,0,0.02)', borderRadius: 8 }}>
                  <Text variant="bodySm" fontWeight="semibold">15-Day Trends:</Text>
                  <div style={{ display: 'flex', gap: 24, marginTop: 8, flexWrap: 'wrap' }}>
                    <Text variant="bodySm">
                      Revenue: {netSalesDirection === Number.POSITIVE_INFINITY 
                        ? <span style={{ color: '#10b981' }}>↑ New sales!</span>
                        : <span style={{ color: netSalesDirection >= 0 ? '#10b981' : '#ef4444' }}>
                            {netSalesDirection >= 0 ? '↑' : '↓'} {Math.abs(netSalesDirection).toFixed(1)}%
                          </span>
                      }
                    </Text>
                    {aovCurr > 0 && (
                      <Text variant="bodySm">
                        AOV: <span style={{ color: aovDeltaPct >= 0 ? '#10b981' : '#ef4444' }}>
                          ${aovCurr.toFixed(2)} ({aovDeltaPct >= 0 ? '↑' : '↓'}{Math.abs(aovDeltaPct).toFixed(1)}%)
                        </span>
                      </Text>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </Layout.Section>

          {/* Sales Chart */}
          <Layout.Section>
            <Card sectioned>
              <div style={{ marginBottom: 16 }}><Text variant="headingMd" as="h2">{cleanText("Sales (Last 30 Days)")}</Text></div>
              <Box padding="4">
                <BarChartComponent 
                  data={salesChartData.length > 0 ? salesChartData : [{ name: 'No Sales', value: 0 }]} 
                  height={260} 
                />
              </Box>
            </Card>
          </Layout.Section>

          {/* SEO Traffic */}
          <Layout.Section>
            <Card sectioned>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <Text variant="headingMd" as="h2">{cleanText("SEO Traffic (Last 7 Days)")}</Text>
                <Tooltip content="Shows sessions by traffic channel. Click (?) to learn more.">
                  <Icon source={QuestionCircleIcon} color="subdued" />
                </Tooltip>
                <button onClick={handleOpenModal} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <Info size={16} style={{ color: '#6A0DAD', marginLeft: 4 }} />
                </button>
              </div>
              {gaStatus.loading ? (
                <Box padding="4" style={{ textAlign: 'center' }}><Text>{cleanText("Checking Google Analytics connection...")}</Text></Box>
              ) : !gaStatus.connected ? (
                <GA4SetupPrompt 
                  variant="card" 
                  feature="SEO traffic breakdown"
                  dismissible={true}
                />
              ) : sessionsByChannelData.length === 0 ? (
                <Box padding="4" style={{ textAlign: 'center' }}><Text>{cleanText("No SEO data available for the last 7 days.")}</Text></Box>
              ) : (
                <Box padding="4">
                  <MultiLineChart data={sessionsByChannelData} lines={channels.map(ch => ({ dataKey: ch, name: ch, dot: true }))} height={300} />
                </Box>
              )}
            </Card>
          </Layout.Section>

          {/* Charts Section - Exact same layout as Demographics */}
          <Layout.Section>
            <div className="grid grid-2">
              {/* Device Breakdown */}
              <Card sectioned>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h3 className="chart-title">{cleanText("Device Breakdown")}</h3>
                  <Tooltip content="Shows the percentage of users browsing on mobile, desktop, and tablet."><Icon source={QuestionCircleIcon} color="subdued" /></Tooltip>
                </div>
                <Box padding="4" className="card--brand-purple">
                  <div style={{ width: '100%', height: 320 }}><DeviceChart deviceCounts={deviceCounts} /></div>
                </Box>
                <div className="recommendation-card" style={{ marginTop: 12 }}>
                  <SectionTitle chip="INSIGHT" title="Device Insights" tone="purple" />
                  <Text as="p">{cleanText(`Desktop: ${deviceCounts.desktop || 0} visits`)}</Text>
                  <Text as="p">{cleanText(`Mobile: ${deviceCounts.mobile || 0} visits`)}</Text>
                  {deviceCounts.mobile > deviceCounts.desktop && <Text as="p" fontWeight="semibold">📱 {cleanText("Mobile-first strategy recommended")}</Text>}
                </div>
              </Card>

              {/* Visitor Loyalty */}
              <Card sectioned>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h3 className="chart-title">{cleanText("Visitor Loyalty")}</h3>
                  <Tooltip content="Breaks down your visitors into new vs. returning users."><Icon source={QuestionCircleIcon} color="subdued" /></Tooltip>
                </div>
                <Box padding="4" className="card--brand-teal">
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie data={loyaltyData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {loyaltyData.map((entry, index) => <Cell key={`cell-${index}`} fill={['#6A0DAD', '#00A8A9'][index % 2]} />)}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <div className="recommendation-card" style={{ marginTop: 12 }}>
                  <SectionTitle chip="INSIGHT" title="Loyalty Metrics" tone="teal" />
                  <Text as="p">{cleanText(`New: ${newCount} (${totalSessions > 0 ? Math.round(newCount / totalSessions * 100) : 0}%)`)}</Text>
                  <Text as="p">{cleanText(`Returning: ${returningCount} (${totalSessions > 0 ? Math.round(returningCount / totalSessions * 100) : 0}%)`)}</Text>
                  {totalSessions > 0 && returningCount / totalSessions < 0.3 && <Text as="p" fontWeight="semibold"> {cleanText("Focus on retention strategies")}</Text>}
                </div>
              </Card>

              {/* Global Reach */}
              <Card sectioned>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h3 className="chart-title">{cleanText("Global Reach")}</h3>
                  <Tooltip content="Shows a map of where your website traffic is coming from."><Icon source={QuestionCircleIcon} color="subdued" /></Tooltip>
                </div>
                <Box padding="4" className="card--brand-purple">
                  <div style={{ width: '100%', height: 320 }}><CountryChoroplethMap countryCounts={languageCounts} /></div>
                </Box>
                <div className="recommendation-card" style={{ marginTop: 12 }}>
                  <SectionTitle chip="INSIGHT" title="Top Language Market" tone="purple" />
                  <Text as="p">{topKnownLanguage ? cleanText(`${topKnownLanguage.lang}: ${topKnownLanguage.count} visits`) : cleanText('—')}</Text>
                  {topKnownLanguage && <Text as="p" fontWeight="semibold">{cleanText(`Consider ${topKnownLanguage.lang === 'en' ? 'English SEO optimization' : `${topKnownLanguage.lang} language support`}`)}</Text>}
                </div>
              </Card>

              {/* Peak Shopping Hours */}
              <Card sectioned>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h3 className="chart-title">{cleanText("Peak Shopping Hours")}</h3>
                  <Tooltip content="Visualizes website traffic throughout the day to help you pinpoint the busiest hours."><Icon source={QuestionCircleIcon} color="subdued" /></Tooltip>
                </div>
                <Box padding="4" className="card--brand-teal">
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={hourlyData} barCategoryGap="20%" barGap={2} maxBarSize={22} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" interval={2} minTickGap={8} tick={{ fontSize: 11 }} tickMargin={10} />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Sessions" fill="#6A0DAD" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <div className="recommendation-card" style={{ marginTop: 12 }}>
                  <SectionTitle chip="INSIGHT" title="Shopping Pattern" tone="teal" />
                  <Text as="p">{peakHour !== null ? cleanText(`Peak traffic at ${peakHour}:00. Schedule promotions 1 hour before for maximum impact.`) : cleanText("Collecting data to identify peak hours.")}</Text>
                </div>
              </Card>
            </div>
          </Layout.Section>

          {/* PRESCRIPTIVE ACTIONS - Beehive Style */}
          <Layout.Section>
            <Card sectioned>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '6px', background: '#6A0DAD', color: 'white', fontSize: '11px', fontWeight: 'bold', marginBottom: 8 }}>{cleanText(stageLabels[stage].toUpperCase())}</div>
                <Text variant="headingLg" as="h2">{cleanText("What To Do Now")}</Text>
                <Text variant="bodyMd" as="p" tone="subdued">{cleanText(`${totalOrders} orders · ${totalSessions.toLocaleString()} visits · ${conversionRate}% conversion`)}</Text>
              </div>

              {smartActions.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {smartActions.map((item, idx) => (
                    <div key={idx} style={{
                      padding: 16,
                      borderRadius: 10,
                      background: item.priority === 'critical' ? 'rgba(239, 68, 68, 0.06)' : item.priority === 'high' ? 'rgba(106, 13, 173, 0.06)' : 'rgba(0, 168, 169, 0.06)',
                      borderLeft: `4px solid ${item.priority === 'critical' ? '#ef4444' : item.priority === 'high' ? '#6A0DAD' : '#00A8A9'}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{
                          minWidth: 28, height: 28, borderRadius: '50%',
                          background: item.priority === 'critical' ? '#ef4444' : item.priority === 'high' ? '#6A0DAD' : '#00A8A9',
                          color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 'bold'
                        }}>{idx + 1}</div>
                        <div>
                          <Text variant="headingSm" as="h4" fontWeight="semibold">{item.label}</Text>
                          <Text variant="bodyMd" tone="subdued" as="p" style={{ marginTop: 4 }}>{item.action}</Text>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Text tone="subdued">{cleanText("Collecting more data to generate recommendations...")}</Text>
              )}

              {/* Biggest Opportunity */}
              <div style={{ marginTop: 24, padding: 20, borderRadius: 12, background: 'linear-gradient(135deg, rgba(106, 13, 173, 0.08), rgba(0, 168, 169, 0.08))', border: '2px solid #6A0DAD' }}>
                <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '6px', background: '#6A0DAD', color: 'white', fontSize: '11px', fontWeight: 'bold', marginBottom: 10 }}>{cleanText("YOUR BIGGEST OPPORTUNITY")}</div>
                <Text variant="bodyLg" as="p">{biggestOpportunity}</Text>
              </div>
            </Card>
          </Layout.Section>

        </Layout>
      </Page>

      {/* SEO Info Modal */}
      {modalActive && (
        <div onClick={handleCloseModal} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 600, maxHeight: '80vh', overflow: 'auto', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 20, fontWeight: 600 }}>{cleanText(seoModalContent.title)}</h2>
              <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', padding: 0, width: 32, height: 32 }}>×</button>
            </div>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{seoModalContent.body}</div>
          </div>
        </div>
      )}
    </LayoutWrapper>
  );
}